import styled from "@emotion/styled";
import { default as React, FC, useState } from "react";
import translate from "src/lib/translate";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import Track from "src/ui/components/Track/Track";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import CustomQuestionnaire from "../../CustomQuestionnaire/CustomQuestionnaire";
import { CenterQuestionnaireContent } from "../../InAppQuestionnaire/InAppQuestionnaire";
import { useSearchParams } from "react-router-dom";
import AppointmentRatingCubit from "src/state/AppointmentRatingCubit/AppointmentRatingCubit";
import { useBlocNext } from "@blac/react";
import Loader from "../../Loader/Loader";
import { HandHighFiveIcon } from "src/constants/icons";
import Translate from "src/ui/components/Translate/Translate";
import { Button } from "@9amhealth/shared";
import Path from "src/constants/path";
import { getAppointmentRatingScreen } from "components/AppQueryPopups/dialogs/appointmentRatingTools/getAppointmentRatingScreen";
import { AppointmentRatingScreen } from "components/AppQueryPopups/dialogs/appointmentRatingTools/AppointmentRatingScreen";

const Wrapper = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: 8.25rem 1rem;
  text-align: center;
  gap: 1rem;
`;

const WidthConstraint = styled.div`
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
`;

const ErrorState: FC<{ onClose: () => void }> = ({ onClose }) => (
  <Wrapper>
    <h4>
      <Translate msg="error.generic.short" />
    </h4>
    <p style={{ textAlign: "center" }}>
      <Translate msg="error.generic.short.tryAgain" />
    </p>
    <Button onClick={onClose} theme="sunrise">
      <Translate msg="returnToHome" />
    </Button>
  </Wrapper>
);

const AppointmentRatingDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [queryParams] = useSearchParams();
  const appointmentId = queryParams.get("appointmentId") ?? "";
  const [beforeBlocMount, setBeforeBlocMount] = useState<boolean>(true);

  const [appointmentRatingState, { loadAppointmentRatingTask }] = useBlocNext(
    AppointmentRatingCubit,
    {
      onMount: () => {
        if (appointmentId) {
          setBeforeBlocMount(false);
          void loadAppointmentRatingTask(appointmentId);
        }
      }
    }
  );

  const onClose = (navigateToHome?: boolean) => {
    AppQueryPopupsController.closePopup();
    if (navigateToHome) {
      navigate(Path.home);
    } else {
      navigate(returnUrl, {
        multiBack: true,
        replace: true
      });
    }
  };

  const onComplete = () => {
    onClose();
  };

  const screen = getAppointmentRatingScreen(appointmentRatingState);

  return (
    <InDialog
      title={translate("yourFeedback")}
      onClose={onClose}
      returnUrl={returnUrl}
      width="min(calc(100% - 4rem), 120rem)"
      height="min(calc(100% - 4rem), 70rem)"
    >
      {(beforeBlocMount || screen === AppointmentRatingScreen.LOADING) && (
        <Loader />
      )}
      {!beforeBlocMount && screen === AppointmentRatingScreen.ERROR && (
        <ErrorState onClose={() => onClose(true)} />
      )}
      {screen === AppointmentRatingScreen.ALREADY_RATED && (
        <Wrapper>
          <HandHighFiveIcon aria-hidden="true" />
          <h4>
            <Translate msg="feedbackAlreadyReceived.title" />
          </h4>
          <p style={{ textAlign: "center" }}>
            <Translate msg="feedbackAlreadyReceived.description" />
          </p>
          <Button onClick={() => onClose(true)} theme="sunrise">
            <Translate msg="returnToHome" />
          </Button>
        </Wrapper>
      )}
      {screen === AppointmentRatingScreen.RATE_NOW &&
        appointmentRatingState.questionnaireId && (
          <CenterQuestionnaireContent>
            <Track
              event={TrackEvent.AppointmentRatingDialog}
              type={TrackType.open}
              data={{ appointmentType: appointmentRatingState.task?.slug }}
            />
            <WidthConstraint>
              <CustomQuestionnaire
                id={appointmentRatingState.questionnaireId}
                onLastStepCompleted={onComplete}
                preventLoadingUserAnswers
                hiddenFields={appointmentRatingState.hiddenFields}
              />
            </WidthConstraint>
          </CenterQuestionnaireContent>
        )}
      {screen === AppointmentRatingScreen.EXPIRED && (
        <Wrapper>
          <h4>
            <Translate msg="feedbackExpired.title" />
          </h4>
          <p style={{ textAlign: "center" }}>
            <Translate msg="feedbackExpired.description" />
          </p>
          <Button onClick={() => onClose(true)} theme="sunrise">
            <Translate msg="returnToHome" />
          </Button>
        </Wrapper>
      )}
    </InDialog>
  );
};

export default AppointmentRatingDialog;
